import { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { UtensilsCrossed, Image, CalendarCheck, Clock, AlertCircle, ChevronRight, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';

const statusLabels = { nouveau: 'Nouveau', en_cours: 'En cours', confirme: 'Confirmé', refuse: 'Refusé' };
const statusColors = { nouveau: 'bg-blue-100 text-blue-800', en_cours: 'bg-yellow-100 text-yellow-800', confirme: 'bg-green-100 text-green-800', refuse: 'bg-red-100 text-red-800' };

export default function Dashboard() {
  const { isModuleEnabled } = useAuth();
  const reservationsEnabled = isModuleEnabled('RESERVATIONS');
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    adminApi.getStats()
      .then(r => setStats(r.data))
      .catch(err => setError(err.response?.data?.detail || 'Impossible de charger les statistiques'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) return (
    <AdminLayout title="Tableau de bord">
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center" data-testid="dashboard-error">
        <AlertCircle className="mx-auto text-red-500 mb-2" size={24} />
        <p className="text-sm text-red-700 font-medium">{error}</p>
      </div>
    </AdminLayout>
  );

  if (!stats) return <AdminLayout title="Tableau de bord"><div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-garden-800 border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;

  const cards = [
    { label: 'Plats actifs', value: stats.total_items, icon: UtensilsCrossed, color: 'bg-garden-100 text-garden-800', href: '/admin/menu', testid: 'dashboard-card-items' },
    { label: 'Catégories', value: stats.total_categories, icon: Clock, color: 'bg-cream text-wood', href: '/admin/menu', testid: 'dashboard-card-categories' },
    { label: 'Photos', value: stats.total_photos, icon: Image, color: 'bg-garden-100 text-garden-800', href: '/admin/photos', testid: 'dashboard-card-photos' },
    ...(reservationsEnabled ? [{ label: 'Réservations', value: stats.total_reservations, icon: CalendarCheck, color: 'bg-cream text-wood', href: '/admin/reservations', testid: 'dashboard-card-reservations' }] : []),
  ];

  return (
    <AdminLayout title="Tableau de bord">
      <div data-testid="admin-dashboard">
        {/* Pending alert (sous le titre) */}
        {reservationsEnabled && stats.pending_reservations > 0 && (
          <Link to="/admin/reservations" className="block mb-6" data-testid="pending-alert-link">
            <div className="bg-sunny/20 border border-sunny rounded-xl p-4 flex items-center gap-3 hover:bg-sunny/30 transition-colors">
              <AlertCircle className="text-yellow-700" size={20} />
              <span className="text-sm font-medium text-yellow-800 flex-1">
                {stats.pending_reservations} réservation(s) en attente de traitement
              </span>
              <ChevronRight size={18} className="text-yellow-700" />
            </div>
          </Link>
        )}

        {/* Stat cards (cliquables) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Link key={c.label} to={c.href} data-testid={c.testid} className="group">
                <Card className="border-garden-200 hover:border-garden-400 hover:shadow-md transition-all cursor-pointer h-full">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-xl ${c.color} group-hover:scale-110 transition-transform`}>
                        <Icon size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-2xl font-heading font-bold text-garden-800">{c.value}</p>
                        <p className="text-xs text-charcoal/60 truncate">{c.label}</p>
                      </div>
                      <ChevronRight size={16} className="text-garden-400 group-hover:text-garden-700 transition-colors hidden sm:block" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Recent reservations */}
        {reservationsEnabled && (
        <>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-heading font-semibold text-garden-800">Dernières réservations</h2>
          <Link to="/admin/reservations" className="text-sm text-garden-600 hover:text-garden-800 font-medium flex items-center gap-1" data-testid="dashboard-all-reservations-link">
            Voir tout <ChevronRight size={14} />
          </Link>
        </div>
        <Link to="/admin/reservations" data-testid="dashboard-reservations-table-link" className="block">
        <div className="bg-white rounded-xl border border-garden-200 overflow-hidden hover:border-garden-400 hover:shadow-md transition-all">
          {stats.recent_reservations?.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="bg-garden-50">
                <tr>
                  <th className="text-left p-3 font-medium text-garden-800">Nom</th>
                  <th className="text-left p-3 font-medium text-garden-800 hidden sm:table-cell">Date</th>
                  <th className="text-left p-3 font-medium text-garden-800 hidden md:table-cell">Personnes</th>
                  <th className="text-left p-3 font-medium text-garden-800">Statut</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_reservations.map((r) => (
                  <tr key={r.id} className="border-t border-garden-100">
                    <td className="p-3 text-charcoal">{r.name}</td>
                    <td className="p-3 text-charcoal/70 hidden sm:table-cell">{r.date} à {r.time}</td>
                    <td className="p-3 text-charcoal/70 hidden md:table-cell">{r.guests}</td>
                    <td className="p-3">
                      <Badge className={`${statusColors[r.status]} border-0 text-xs`}>
                        {statusLabels[r.status]}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-center py-8 text-charcoal/50 text-sm">Aucune réservation récente</p>
          )}
        </div>
        </Link>
        </>
        )}
      </div>
    </AdminLayout>
  );
}
